


















































import { defineComponent, ref, PropType, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { ROLES } from "@/const";
import AvatarDescription from "@/components/AvatarDescription";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseInput from "@/components/BaseInput";
import BaseSpinner from "@/components/BaseSpinner";
import CustomScroll from "@/components/CustomScroll";
import ListItem from "@/components/ListItem";
import UserChip from "@/components/UserChip";

const USER_FRAGMENT: FragmentFields<UserPublicData> = [
  "id",
  "screenName",
  "username",
  { onlineStatus: ["isOnline"] },
  { avatar: ["urlPart", "uuid", "metadata"] },
];

export default defineComponent({
  name: "UserSearchAndSelect",
  components: { BaseSpinner, CustomScroll, BaseCheckbox, AvatarDescription, ListItem, UserChip, BaseInput },
  props: {
    label: { type: String },
    role: { type: String, default: ROLES.CREATOR },
    maxSelectedUsers: { type: Number, default: 7 },
    selectedUserIds: { type: Array as PropType<string[]>, default: () => [] },
    excludeUserIds: { type: Array as PropType<string[]>, default: () => [] },
    scrollable: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const usersList = ref<UserPublicData[]>([]);
    const selectedUsersList = ref<UserPublicData[]>([]);
    const searchValue = ref("");

    const searchUsers = debounce(async () => {
      if (!searchValue.value) {
        usersList.value = [];
        return;
      }
      try {
        loading.value = true;
        const response = await providers.UserPublicDataProvider.search(
          {
            data: {
              role: props.role ? props.role : undefined,
              relevantTerm: searchValue.value,
              page: 0,
              size: 20,
            },
          },
          USER_FRAGMENT
        );
        usersList.value = response.result.filter(
          i => i.id !== authUserStrict.value.id && !props.excludeUserIds.includes(i.id)
        );
      } finally {
        loading.value = false;
      }
    }, 500);

    const removeUser = (id: string) => {
      selectedUsersList.value = selectedUsersList.value.filter(i => i.id !== id);
    };

    const toggleSelectUser = (user: UserPublicData) => {
      if (props.selectedUserIds.includes(user.id)) {
        removeUser(user.id);
      } else if (props.selectedUserIds.length < props.maxSelectedUsers) {
        selectedUsersList.value = [...selectedUsersList.value, user];
      }
    };

    const isUserSelected = (id: string) => {
      return props.selectedUserIds.some(i => i === id);
    };

    watch(selectedUsersList, value => {
      emit(
        "selectedUserIds",
        value.map(i => i.id)
      );
    });

    return {
      loading,
      searchValue,
      usersList,
      selectedUsersList,
      toggleSelectUser,
      isUserSelected,
      removeUser,
      searchUsers,
    };
  },
});
